import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * مسارُ التنقّل (24 أكتوبر 2026 — الكادر: «لما يفتحون النماذج أو يدخلون داخل الأهداف … يبون يطلع الدايركشنز اللي دخلوا منها
 * عشان يرجعون لها حبّة حبّة، أو يعرفون وين يتنقّلون»).
 *
 * سطرٌ فوق عنوان الصفحة: «السجلات › النماذج الرقمية › ملف الطالب › التقييم»، وكلُّ حلقةٍ قبل الأخيرة رابطٌ يرجع إليها.
 * يُبنى وحده في كل صفحةٍ داخل القشرة من **بند القائمة** الذي تقع تحته الصفحة ومن رابط «العودة» إن كان — فلا تكتبه كلُّ
 * صفحة؛ والصفحةُ التي تعرف طريقاً أصدق (ملفُّ طالبٍ باسمه، قسمٌ ومجالٌ داخل بنك الأهداف) تمرّر حلقاتها في `crumbs`.
 * الصفحةُ التي هي بندُ القائمة نفسُه بلا عودة لا مسارَ لها: القائمةُ تظلّلها والعنوانُ يكفي.
 */
export interface Crumb { label: string; to?: string; onClick?: () => void }

export interface NavTrailItem { to: string; label: string; group: string }
/** بنودُ قائمة صاحب الحساب بأسمائها المترجمة — تضعها القشرة، ويقرؤها `PageHeader` */
export const NavTrailContext = createContext<readonly NavTrailItem[] | null>(null);

/** البندُ الذي تقع تحته الصفحة: أطولُ مسارٍ في القائمة يساويها أو يبدأ به مسارُها */
export function navItemFor(items: readonly NavTrailItem[], pathname: string): NavTrailItem | null {
  let best: NavTrailItem | null = null;
  for (const it of items) {
    const hit = pathname === it.to || pathname.startsWith(`${it.to}/`);
    if (hit && (!best || it.to.length > best.to.length)) best = it;
  }
  return best;
}

/** «العودة إلى ملف الطالب» ← «ملف الطالب» */
export const crumbLabelFromBack = (label: string) => label.replace(/^(?:ال)?عودة\s+(?:إلى|الى)\s+/, '').trim() || label;

/**
 * المسارُ التلقائي: [مجموعةُ القائمة] › [بندُها إن لم تكن الصفحةَ نفسَها] › [العودة إن اختلفت عنه] › العنوان.
 * `null` حين لا شيءَ يُضاف على تظليل القائمة.
 */
export function autoCrumbs(items: readonly NavTrailItem[] | null, pathname: string, title: string, back?: { to: string; label: string }): Crumb[] | null {
  if (!items) return null;
  const item = navItemFor(items, pathname);
  const deeper = !!item && pathname !== item.to;
  const backPath = back?.to.split('?')[0];
  const extraBack = !!back && (!item || backPath !== item.to);
  if (!deeper && !extraBack) return null;
  const out: Crumb[] = [];
  if (item) out.push({ label: item.group });
  if (item && deeper) out.push({ label: item.label, to: item.to });
  if (back && extraBack) out.push({ label: crumbLabelFromBack(back.label), to: back.to });
  out.push({ label: title });
  return out;
}

export function useAutoCrumbs(title: string, back?: { to: string; label: string }): Crumb[] | null {
  const items = useContext(NavTrailContext);
  const { pathname } = useLocation();
  return autoCrumbs(items, pathname, title, back);
}

/**
 * أولُ الطريق للصفحات التي تمرّر حلقاتها: مجموعةُ القائمة وبندُها الذي يقع تحته `path` (افتراضاً الصفحةُ نفسُها).
 * لا شيء إن لم يكن في قائمة صاحب الحساب بندٌ له — فلا رابطَ إلى شاشةٍ لا يدخلها.
 */
export function useNavRoot(path?: string): Crumb[] {
  const items = useContext(NavTrailContext);
  const { pathname } = useLocation();
  const item = items ? navItemFor(items, path ?? pathname) : null;
  return item ? [{ label: item.group }, { label: item.label, to: item.to }] : [];
}

